import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion } from "framer-motion";
import {
  Search, FileText, CheckCircle2, Clock, XCircle,
  RefreshCw, Download, Wallet, TrendingUp, CalendarRange,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as settlementService from "@/shared/api/services/settlementService";
import { auth } from "@/shared/api/graphqlClient";
import { exportToCsv } from "@/shared/utils/csvExport";

const statusCfg = {
  completed:  { icon:CheckCircle2, color:"#10b981", bg:"rgba(16,185,129,.1)",  border:"rgba(16,185,129,.25)",  label:"Completed"  },
  pending:    { icon:Clock,        color:"#f59e0b", bg:"rgba(245,158,11,.1)",  border:"rgba(245,158,11,.25)",  label:"Pending"    },
  processing: { icon:RefreshCw,   color:"#38bdf8", bg:"rgba(56,189,248,.1)",  border:"rgba(56,189,248,.25)",  label:"Processing" },
  failed:     { icon:XCircle,      color:"#ef4444", bg:"rgba(239,68,68,.1)",   border:"rgba(239,68,68,.25)",   label:"Failed"     },
};

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, color, delay }) {
  const [h, setH] = useState(false);
  return (
    <motion.div
      initial={{ opacity:0, y:22 }}
      animate={{ opacity:1, y:0 }}
      transition={{ delay, duration:.45, ease:[0.34,1.56,0.64,1] }}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{
        background:"linear-gradient(135deg,rgba(13,27,46,.88),rgba(15,30,55,.88))",
        border:`1px solid ${h?color+"45":color+"20"}`,
        borderRadius:"18px", padding:"20px", position:"relative", overflow:"hidden",
        transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
        transform: h?"translateY(-7px) scale(1.03)":"translateY(0) scale(1)",
        boxShadow: h?`0 18px 44px ${color}25, 0 0 0 1px ${color}18`:"0 2px 10px rgba(0,0,0,.28)",
        cursor:"default",
      }}
    >
      <div style={{ position:"absolute", inset:0, background: h?`radial-gradient(circle at 80% 20%,${color}14 0%,transparent 65%)`:"none", transition:"all .4s ease", pointerEvents:"none" }} />
      {h && <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px", background:`linear-gradient(90deg,transparent,${color}80,transparent)`, borderRadius:"18px 18px 0 0" }} />}
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"12px" }}>
        <p style={{ fontSize:"10px", color:"#64748b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em" }}>{label}</p>
        <div style={{ width:"36px", height:"36px", borderRadius:"10px", background:`${color}18`, border:`1px solid ${color}30`, display:"flex", alignItems:"center", justifyContent:"center", transition:"transform .38s cubic-bezier(0.34,1.56,0.64,1)", transform: h?"rotate(-8deg) scale(1.18)":"none" }}>
          <Icon style={{ width:"16px", height:"16px", color }} />
        </div>
      </div>
      <p style={{ fontSize:"22px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.02em" }}>{value}</p>
    </motion.div>
  );
}

/* ─── Status badge ─── */
function SBadge({ status }) {
  const c = statusCfg[status?.toLowerCase()] || statusCfg.pending;
  const Icon = c.icon;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", background:c.bg, border:`1px solid ${c.border}`, fontSize:"11px", fontWeight:600, color:c.color }}>
      <Icon style={{ width:"11px", height:"11px", ...(status?.toLowerCase()==="processing"?{animation:"stspin 1.2s linear infinite"}:{}) }} />
      {c.label}
    </span>
  );
}

/* ─── Main ─── */
export default function ResellerSettlements() {
  const { t, language } = useLanguage();
  const [search, setSearch]   = useState("");
  const [statusF, setStatusF] = useState("all");
  const [sf, setSf]           = useState(false);
  const resellerName = auth.getUserData()?.name || "Reseller";

  const { data: settlementData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["reseller-settlements", search, statusF],
    queryFn: () => settlementService.getResellerSettlements({
      page: 1,
      limit: 200,
      search: search || undefined,
      filters: statusF !== "all" ? { status: statusF.toUpperCase() } : undefined,
    }),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    staleTime: 5000,
  });

  const settlements = settlementData?.data || [];

  const filtered = settlements.filter(s => {
    const ms = !search ||
      s.id?.toLowerCase().includes(search.toLowerCase()) ||
      s.merchant_code?.toLowerCase().includes(search.toLowerCase());
    const mst = statusF === "all" || s.status?.toLowerCase() === statusF;
    return ms && mst;
  });

  const completedSettlements = settlements.filter(s=>s.status === "COMPLETED");
  const totalSettled  = completedSettlements.reduce((sum,s)=>sum+(s.net_amount||0),0);
  const pendingCount  = settlements.filter(s=>["PENDING","PROCESSING"].includes(s.status)).length;

  const STATUSES = ["all","completed","pending","processing","failed"];

  const handleExport = useCallback(() => {
    exportToCsv(filtered, {
      headers: [
        "id",
        "merchant",
        "merchant_code",
        "status",
        "net_amount",
        "currency",
        "period_start",
        "period_end",
        "createdAt",
      ],
      filename: `reseller-settlements-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: (row, key) => {
        if (key === "merchant") return row.merchant?.name || "";
        return row[key] ?? "";
      },
    });
  }, [filtered]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <ResellerLayout currentPage="ResellerSettlements" resellerName={resellerName}>
      <style>{`@keyframes stspin{to{transform:rotate(360deg)}} @keyframes sspin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth:"1400px" }}>

        {/* Header */}
        <motion.div initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} style={{ marginBottom:"28px" }}>
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:"16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
              <div style={{ width:"44px", height:"44px", borderRadius:"13px", background:"linear-gradient(135deg,#10b981,#059669)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 18px rgba(16,185,129,.45)" }}>
                <FileText style={{ width:"20px", height:"20px", color:"#fff" }} />
              </div>
              <div>
                <h1 style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.02em", margin:0 }}>
                  {t("settlements")||"Settlements"}
                </h1>
                <p style={{ fontSize:"13px", color:"#475569", margin:0 }}>
                  {t("totalSettled")||"Total settled"}: <span style={{ color:"#10b981", fontWeight:700 }}>฿{totalSettled.toLocaleString()}</span>
                </p>
              </div>
            </div>
            <div style={{ display:"flex", gap:"8px" }}>
              <button
                onClick={handleExport}
                disabled={!filtered.length}
                style={{ display:"flex", alignItems:"center", gap:"6px", padding:"10px 16px", borderRadius:"12px", background:"rgba(56,189,248,.08)", border:"1px solid rgba(56,189,248,.2)", color:"#38bdf8", fontSize:"13px", fontWeight:600, cursor:"pointer", transition:"all .35s cubic-bezier(0.34,1.56,0.64,1)" }}
                onMouseEnter={e=>{ e.currentTarget.style.background="rgba(56,189,248,.16)"; e.currentTarget.style.transform="scale(1.05)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.background="rgba(56,189,248,.08)"; e.currentTarget.style.transform="scale(1)"; }}
              >
                <Download style={{ width:"14px", height:"14px" }} />{t("export")||"Export"}
              </button>
              <button onClick={handleRefresh}
                disabled={isFetching}
                style={{ width:"40px", height:"40px", borderRadius:"12px", background:"rgba(16,185,129,.08)", border:"1px solid rgba(16,185,129,.2)", color:"#10b981", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)", flexShrink:0 }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="rotate(180deg) scale(1.14)"; e.currentTarget.style.background="rgba(16,185,129,.18)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="rotate(0) scale(1)"; e.currentTarget.style.background="rgba(16,185,129,.08)"; }}
              >
                <RefreshCw style={{ width:"15px", height:"15px", animation: isFetching ? "sspin 1s linear infinite" : undefined }} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:"14px", marginBottom:"24px" }}>
          <StatCard label={t("totalSettled")||"Total Settled"}    value={`฿${totalSettled.toLocaleString()}`} icon={Wallet}      color="#10b981" delay={0}    />
          <StatCard label={t("completed")||"Completed"}            value={completedSettlements.length}          icon={CheckCircle2} color="#10b981" delay={0.05} />
          <StatCard label={t("pending")||"Pending"}                value={pendingCount}                         icon={Clock}        color="#f59e0b" delay={0.1}  />
          <StatCard label="Total"                                  value={settlements.length}                   icon={TrendingUp}   color="#38bdf8" delay={0.15} />
        </div>

        {/* Filters */}
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.2 }}
          style={{ display:"flex", flexWrap:"wrap", gap:"10px", marginBottom:"18px", alignItems:"center" }}>
          <div style={{ position:"relative", flex:"1 1 200px", maxWidth:"360px" }}>
            <Search style={{ position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", width:"15px", height:"15px", color: sf?"#10b981":"#64748b", transition:"color .2s" }} />
            <input value={search} onChange={e=>setSearch(e.target.value)} onFocus={()=>setSf(true)} onBlur={()=>setSf(false)}
              placeholder={`${t("search")||"Search"} ID, merchant...`}
              style={{ width:"100%", padding:"10px 14px 10px 40px", borderRadius:"12px", background:"rgba(13,27,46,.85)", border:`1px solid ${sf?"rgba(16,185,129,.42)":"rgba(16,185,129,.12)"}`, color:"#e2e8f0", fontSize:"13px", outline:"none", transition:"all .3s ease", boxShadow: sf?"0 0 0 3px rgba(16,185,129,.09)":"none" }}
            />
          </div>

          <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
            {STATUSES.map(s=>(
              <button key={s} onClick={()=>setStatusF(s)} style={{
                padding:"8px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:600, cursor:"pointer", textTransform:"capitalize",
                transition:"all .38s cubic-bezier(0.34,1.56,0.64,1)",
                background: statusF===s ? "linear-gradient(135deg,rgba(16,185,129,.25),rgba(5,150,105,.2))" : "rgba(13,27,46,.65)",
                border: statusF===s ? "1px solid rgba(16,185,129,.42)" : "1px solid rgba(16,185,129,.1)",
                color: statusF===s ? "#34d399" : "#64748b",
                transform: statusF===s ? "scale(1.06)" : "scale(1)",
                boxShadow: statusF===s ? "0 4px 14px rgba(16,185,129,.2)" : "none",
              }}>
                {s==="all"?"All":s.charAt(0).toUpperCase()+s.slice(1)}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:.25 }}
          style={{ background:"linear-gradient(135deg,rgba(13,27,46,.92) 0%,rgba(15,30,55,.92) 100%)", border:"1px solid rgba(16,185,129,.1)", borderRadius:"20px", overflow:"hidden" }}
        >
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid rgba(16,185,129,.08)", background:"rgba(8,14,26,.6)" }}>
                  {[t("settlement")||"Settlement", t("merchant")||"Merchant", t("amount")||"Amount", t("status")||"Status", t("period")||"Period"].map((h,i)=>(
                    <th key={i} style={{ padding:"13px 18px", textAlign: i===2?"right":"left", fontSize:"10px", fontWeight:700, color:"#334155", textTransform:"uppercase", letterSpacing:"0.1em", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} style={{ textAlign:"center", padding:"64px", color:"#475569" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"14px" }}>
                      <RefreshCw style={{ width:"28px", height:"28px", color:"#10b981", animation:"sspin 1s linear infinite" }} />
                      <span style={{ fontSize:"13px" }}>{t("loading")||"Loading…"}</span>
                    </div>
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign:"center", padding:"72px 24px", color:"#334155" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"14px" }}>
                      <FileText style={{ width:"52px", height:"52px", color:"#1e293b" }} />
                      <p style={{ margin:0, fontSize:"14px" }}>{t("noSettlements")||"No settlements found"}</p>
                    </div>
                  </td></tr>
                ) : filtered.map((s,i)=>(
                  <motion.tr key={s.id||i}
                    initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay:i*.025 }}
                    style={{ borderBottom:"1px solid rgba(16,185,129,.04)", transition:"background .2s ease", cursor:"default" }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(16,185,129,.04)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    {/* Settlement ID */}
                    <td style={{ padding:"14px 18px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                        <div style={{ width:"36px", height:"36px", borderRadius:"10px", background:"rgba(16,185,129,.12)", border:"1px solid rgba(16,185,129,.22)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <FileText style={{ width:"15px", height:"15px", color:"#10b981" }} />
                        </div>
                        <span style={{ fontFamily:"monospace", fontSize:"12px", color:"#34d399", fontWeight:600 }}>
                          {s.id}
                        </span>
                      </div>
                    </td>
                    {/* Merchant */}
                    <td style={{ padding:"14px 18px" }}>
                      <span style={{ fontSize:"13px", color:"#94a3b8" }}>{s.merchant?.name || s.merchant_code || t("nA") || "N/A"}</span>
                    </td>
                    {/* Amount */}
                    <td style={{ padding:"14px 18px", textAlign:"right" }}>
                      <span style={{ fontSize:"16px", fontWeight:800, color:"#f0f9ff" }}>
                        ฿{s.net_amount?.toLocaleString()}
                      </span>
                    </td>
                    {/* Status */}
                    <td style={{ padding:"14px 18px" }}>
                      <SBadge status={s.status} />
                    </td>
                    {/* Period */}
                    <td style={{ padding:"14px 18px" }}>
                      {s.period_start && s.period_end ? (
                        <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
                          <CalendarRange style={{ width:"13px", height:"13px", color:"#475569" }} />
                          <span style={{ fontSize:"12px", color:"#64748b" }}>
                            {format(new Date(s.period_start), language==="th"?"d MMM":"MMM d", { locale:language==="th"?th:enUS })}
                            {" — "}
                            {format(new Date(s.period_end), language==="th"?"d MMM":"MMM d", { locale:language==="th"?th:enUS })}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize:"12px", color:"#334155" }}>{t("nA")||"N/A"}</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          {filtered.length > 0 && (
            <div style={{ padding:"14px 18px", borderTop:"1px solid rgba(16,185,129,.06)", display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(8,14,26,.4)" }}>
              <span style={{ fontSize:"12px", color:"#475569" }}>{filtered.length} settlements</span>
              <span style={{ fontSize:"12px", color:"#10b981", fontWeight:700 }}>
                ฿{filtered.filter(s=>s.status === "COMPLETED").reduce((sum,s)=>sum+(s.net_amount||0),0).toLocaleString()} settled
              </span>
            </div>
          )}
        </motion.div>
      </div>
    </ResellerLayout>
  );
}